import { ValuesCache } from '@leverege/value-cache';
import equals from 'fast-deep-equal';
import { Plugins } from '@leverege/plugin';
import { v4 } from '@leverege/base62-util';
import { OwnerCoownerApiHandler } from './utils/index.js';
import RsrcReducer from './RsrcReducer.js';
import Rsrc from './Rsrc.js';
class ApiUserSettingsDelegate {
  /**
   * 
   * @param {string} options.profile a string representing the user "type", eg "installer"
   * @param {Array} options.fallback an array of fallback configurations
   * @param {function} options.dispatch a redux dispatch function
   * @param {string} options.basePath a string for the base path UI rsrcs live under, eg "ui"
   * @param {string} options.userId the id of the logged in user for the ui
   * @param {string} options.projectId the id of the ui's project
   */
  constructor(options) {
    // has a profile, and fallback configuration
    this.options = options;
    this.preloaded = false;
    this.profile = this.getDefaultProfile(options?.profile);
    this.fallback = this.getDefaultFallback(options?.fallback, options);
    this.dispatch = this.getDefaultDispatch(options?.dispatch);
    this.basePath = this.getDefaultBasePath(options?.basePath);
    this.versionsCache = new ValuesCache({
      equals
    });
    this.defaultInterceptCache = new ValuesCache({
      equals
    });
    this.intercepts = {};
    this.defaults = {};
  }
  setProfile(profile) {
    this.profile = this.getDefaultProfile(profile);
  }

  // setup
  getDefaultBasePath(basePath) {
    const bp = typeof basePath === 'string' ? basePath : 'ui';
    if (bp.endsWith('/')) {
      return bp.slice(0, -1);
    }
    return bp;
  }
  getDefaultProfile(profile) {
    const p = typeof profile === 'string' ? profile : 'default';
    if (p.endsWith('/')) {
      return p.slice(0, -1);
    }
    return p;
  }
  getDefaultFallback(fallback, options) {
    const {
      exclude
    } = options;
    let fb = fallback;
    if (!Array.isArray(fb)) {
      fb = [];
      if (options?.userId && !exclude?.userId) {
        fb.push({
          ownerId: options.userId,
          ownerType: 'user'
        });
      }
      if ((options?.systemId || process.env?.systemId) && !exclude?.systemId) {
        fb.push({
          ownerId: options?.systemId || process?.env?.systemId,
          ownerType: 'system',
          projectId: options.projectId || process?.env?.projectId
        });
      }
      if ((options?.projectId || process?.env?.projectId) && !exclude?.projectId) {
        fb.push({
          ownerId: options.projectId || process?.env?.projectId,
          ownerType: 'project'
        });
      }
    }
    return fb;
  }
  getDefaultDispatch(dispatch) {
    return typeof dispatch === 'function' ? dispatch : () => {};
  }
  // end setup

  // manage rsrcs
  getRsrcPath(path, profile = this.profile) {
    const {
      basePath
    } = this;
    return `${basePath}${profile ? `/${profile}` : ''}${path ? `/${path}` : ''}`;
  }
  getVersionPath(path, version = null) {
    const {
      basePath
    } = this;
    return `${basePath}/version${path ? `/${path}` : ''}${version ? `/${version}` : ''}`;
  }
  getVersionNameFromPath(path, fullVersion) {
    const {
      basePath
    } = this;
    return fullVersion?.replace(`${basePath}/version${path ? `/${path}` : ''}/`, '');
  }
  calcVersions = (path, rawVersions) => {
    if (!rawVersions || !rawVersions.items) {
      return rawVersions;
    }
    return {
      ...rawVersions,
      items: rawVersions.items?.map(v => ({
        ...v,
        name: this.getVersionNameFromPath(path, v.path)
      }))
    };
  };
  prepareVersionList(path, fallback, rawVersions) {
    // version fallback is always length 1
    return this.versionsCache.calc(`${this.pathForType(fallback?.[0] || {})}.${path}`, this.calcVersions, path, rawVersions);
  }
  getFallbackByScope(fallback, opts, defaultValue = [fallback[this.fallback.length - 1]]) {
    if (opts?.scope) {
      const piece = fallback?.find(fb => fb?.ownerType === opts.scope);
      if (piece) {
        return [piece];
      }
    }
    return defaultValue;
  }

  // needs resolved path
  getLocal(state, path, oPath, fallback) {
    for (let i = 0; i < fallback.length; i++) {
      const {
        ownerId,
        coownerId
      } = fallback[i];
      const rsrc = RsrcReducer.get(state, ownerId, coownerId, path);
      if (rsrc) {
        return this.procDefaultAndIntercepts(path, oPath, rsrc.data);
      }
    }
    return this.procDefaultAndIntercepts(path, oPath);
  }
  getLocalChildren(state, path, oPath, fallback) {
    for (let i = 0; i < fallback.length; i++) {
      const {
        ownerId,
        coownerId
      } = fallback[i];
      const list = RsrcReducer.list(state, ownerId, coownerId, path);
      if (list) {
        const toReturn = {
          ...list
        };
        if (toReturn?.items) {
          toReturn.items = toReturn?.items.map(val => ({
            ...val,
            data: this.procDefaultAndIntercepts(path, oPath, val?.data)
          }));
        }
        return toReturn;
      }
    }
    return null;
  }

  // needs resolved path
  getOwnerType(state, path, fallback) {
    for (let i = 0; i < fallback.length; i++) {
      const {
        ownerId,
        coownerId,
        ownerType
      } = fallback[i];
      const rsrc = RsrcReducer.get(state, ownerId, coownerId, path);
      if (rsrc) {
        return ownerType;
      }
    }
    return null;
  }
  fallbackIsLoading(state, path, fallback) {
    for (let i = 0; i < fallback.length; i++) {
      const {
        ownerId,
        coownerId
      } = fallback[i];
      const status = RsrcReducer.status(state, ownerId, coownerId, path);
      if (status?.status === 'loading') {
        return true;
      }
    }
    return false;
  }

  // needs resolved path
  shouldFetch(state, path, fallback) {
    let allErrorOrNonExistent = true;
    for (let i = 0; i < fallback.length; i++) {
      const {
        ownerId,
        coownerId
      } = fallback[i];
      const val = RsrcReducer.get(state, ownerId, coownerId, path);
      if (val) {
        return false;
      }
      const status = RsrcReducer.status(state, ownerId, coownerId, path);
      if (status?.status === 'loading' && !status?.error && status?.exists !== false) {
        return true;
      }
      if (!status?.error && status?.exists !== false) {
        allErrorOrNonExistent = false;
      }
    }
    return !allErrorOrNonExistent;
  }

  // needs resolved path
  shouldFetchChildren(state, path, fallback, opts) {
    let allError = true;
    for (let i = 0; i < fallback.length; i++) {
      const {
        ownerId,
        coownerId
      } = fallback[i];
      const val = RsrcReducer.list(state, ownerId, coownerId, path);
      if (val) {
        return false;
      }
      const status = RsrcReducer.status(state, ownerId, coownerId, path);
      if (status?.status === 'loading' && !status?.error) {
        return true;
      }
      if (!status || !status.error) {
        allError = false;
      }
    }
    return !allError;
  }
  resourceApiForType(fallback) {
    const {
      ownerId,
      coownerId
    } = fallback;
    const handler = OwnerCoownerApiHandler.handlerForFallback(fallback);
    return handler?.value(ownerId, coownerId, fallback);
  }
  pathForType(fallback) {
    const {
      ownerId,
      coownerId
    } = fallback;
    const handler = OwnerCoownerApiHandler.handlerForFallback(fallback);
    return handler?.path(ownerId, coownerId, fallback);
  }

  // needs resolved path
  doFetch(path, fallback, command = 'get', ...args) {
    const rsrc = new Rsrc({
      ownerId: fallback.ownerId,
      coownerId: fallback.coownerId,
      path,
      resourceApi: this.resourceApiForType(fallback)
    });
    if (rsrc[command]) {
      return this.dispatch(rsrc[command](...args));
    }
    return null;
  }

  // needs resolved path and original path for default/intercept
  // eslint-disable-next-line default-param-last
  async fetch(path, oPath, fallback, command = 'get', throwErr, ...args) {
    for (let i = 0; i < fallback.length; i++) {
      let check;
      try {
        check = await this.doFetch(path, fallback[i], command, ...args);
      } catch (err) {
        // ignore errors?
        if (err && err.status !== 404 && err.status !== 403) {
          console.log('rsrc fetch err', err);
        }
        if (throwErr && i === fallback.length - 1) {
          throw err;
        }
      }
      if (check) {
        // only list in event of versions right now
        // so assume oPath applies to everything in list
        if (command === 'list') {
          const toReturn = {
            ...check
          };
          if (toReturn?.items) {
            toReturn.items = toReturn?.items.map(val => ({
              ...val,
              data: this.procDefaultAndIntercepts(path, oPath, val?.data)
            }));
          }
          return toReturn;
        }
        return this.procDefaultAndIntercepts(path, oPath, check.data);
      }
    }
    if (command === 'get') {
      return this.procDefaultAndIntercepts(path, oPath);
    }
    // they all failed
    return undefined;
  }
  async preloadAll() {
    const fallback = this.fallback;
    for (let i = 0; i < fallback.length; i++) {
      const limit = 1000;
      let offset = 0,
        s = null,
        total = 0;
      do {
        s = await this.doFetch(this.getRsrcPath(), fallback[i], 'list', {
          limit,
          offset
        });
        offset += limit;
        total = s?.total || 0;
      } while (offset < total);
    }
    this.preloaded = true;
  }
  getIf(state, originalPath, fullPath, fallback, opts) {
    if (opts?.cacheOnly !== true && (this.shouldFetch(state, fullPath, fallback) || opts?.noCache === true)) {
      const p = this.fetch(fullPath, originalPath, fallback, 'get', false, {
        throwNotFound: false
      }, {
        noCache: opts?.noCache
      });
      if (opts?.returnPromise !== false) {
        return p;
      }
      if (!this.preloaded) {
        return null;
      }
    }
    return this.getLocal(state, fullPath, originalPath, fallback);
  }
  async updateOrCreate(originalPath, fullPath, fallback, value) {
    // always setting data wholesale
    const v = await this.fetch(fullPath, originalPath, fallback, 'update', false, [{
      path: 'data',
      value
    }]);
    // assume we failed because it doesn't exist?
    if (v) {
      return v;
    }
    return this.fetch(fullPath, originalPath, fallback, 'create', true, value);
  }

  // intercepts and defaults (use unresolved path so are ignorant of persona or version)
  addIntercept(path, intercept, id = v4()) {
    // handle the case where the only passed in argument is the whole plugin
    let plg;
    if (typeof path === 'string') {
      plg = {
        id,
        intercept,
        path
      };
    } else {
      plg = path;
    }
    if (typeof plg?.intercept === 'function') {
      Plugins.add('UserSettings.Intercept', {
        id,
        intercept,
        path
      });
      return {
        id,
        intercept,
        path
      };
    }
    return null;
  }
  removeIntercept(path, fnOrId) {
    let id = fnOrId;
    if (typeof fnOrId === 'function') {
      const intercepts = Plugins.get('UserSettings.Intercept', intercept => path === intercept.path && intercept?.intercept === fnOrId);
      if (intercepts.length === 1) {
        id = intercepts[0].id;
      }
    }
    Plugins.remove('UserSettings.Intercept', id);
  }
  getIntercepts(path) {
    return Plugins.get('UserSettings.Intercept', intercept => intercept?.path === path);
  }
  calcIntercepts(path, value) {
    let val = value;
    const intercepts = this.getIntercepts(path);
    if (Array.isArray(intercepts)) {
      for (let i = 0; i < intercepts.length; i++) {
        val = typeof intercepts[i]?.intercept === 'function' ? intercepts[i].intercept(val) : val;
      }
    }
    return val;
  }
  getDefaultPluginId(path) {
    return `UserSettings.Default.${path}`;
  }
  addDefault(path, fnOrObj) {
    // handle the case where the only passed in argument is the whole plugin
    let plg;
    if (typeof path === 'string') {
      plg = {
        id: this.getDefaultPluginId(path),
        path,
        default: typeof fnOrObj === 'function' ? fnOrObj : () => fnOrObj
      };
    } else {
      plg = {
        ...path,
        // don't let caller set the id, it needs to be based on path
        id: this.getDefaultPluginId(path.path)
      };
    }
    return Plugins.replace('UserSettings.Default', plg);
  }
  getDefault(path) {
    return Plugins.get('UserSettings.Default', plg => plg.path === path)?.[0]?.default?.();
  }
  removeDefault(path) {
    return Plugins.remove('UserSettings.Default', this.getDefaultPluginId(path));
  }
  getDefaultValue(path) {
    return this.getDefault(path);
  }
  calcDefaultAndIntercepts = (path, value, defaultValue) => {
    const val = value || defaultValue;
    return this.calcIntercepts(path, val);
  };
  procDefaultAndIntercepts(path, oPath, value) {
    return this.versionsCache.calc(path, this.calcDefaultAndIntercepts, oPath, value, this.getDefaultValue(oPath), this.getIntercepts(oPath));
  }
  // end manage rsrcs

  // interface functions for current
  get(state, path, opts) {
    return this.getIf(state, path, this.getRsrcPath(path), this.fallback, opts);
  }
  getProfile(state, profile, path, opts) {
    return this.getIf(state, path, this.getRsrcPath(path, profile), this.fallback, opts);
  }
  isLoading(state, path, opts) {
    return this.fallbackIsLoading(state, this.getRsrcPath(path), this.fallback);
  }
  getScope(state, path) {
    const rPath = this.getRsrcPath(path);
    return this.getOwnerType(state, rPath, this.fallback);
  }
  getProfileScope(state, profile, path) {
    const rPath = this.getRsrcPath(path, profile);
    return this.getOwnerType(state, rPath, this.fallback);
  }
  async set(path, value) {
    const fallback = this.getFallbackByScope(this.fallback, {
      scope: 'user'
    }, [this.fallback[0]]);
    return this.updateOrCreate(path, this.getRsrcPath(path), fallback, value);
  }
  reset(path) {
    // only want to delete the most "local"?
    const fallback = this.getFallbackByScope(this.fallback, {
      scope: 'user'
    }, [this.fallback[0]]);
    return this.fetch(this.getRsrcPath(path), path, fallback, 'delete', true);
  }

  // current version stuff
  getVersion(state, path, version, opts) {
    const fallback = this.getFallbackByScope(this.fallback, {
      scope: 'user'
    }, [this.fallback[0]]);
    return this.getIf(state, path, this.getVersionPath(path, version), fallback, opts);
  }
  getVersions(state, path, opts) {
    const vPath = this.getVersionPath(path);
    const fallback = this.getFallbackByScope(this.fallback, {
      scope: 'user'
    }, [this.fallback[0]]);
    if (opts?.cacheOnly !== true && (this.shouldFetchChildren(state, vPath, fallback, opts) || opts?.noCache === true)) {
      const p = this.fetch(vPath, path, fallback, 'list', false, opts).then(rawVersions => this.prepareVersionList(path, fallback, rawVersions));
      if (opts?.returnPromise !== false) {
        return p;
      }
      if (!this.preloaded) {
        return null;
      }
    }
    return this.prepareVersionList(path, fallback, this.getLocalChildren(state, vPath, path, fallback));
  }
  async setVersion(path, version, value, opts) {
    const fallback = this.getFallbackByScope(this.fallback, {
      scope: 'user'
    }, [this.fallback[0]]);
    return this.updateOrCreate(path, this.getVersionPath(path, version), fallback, value);
  }
  renameVersion(path, oldVersionName, newVersionName, opts) {
    const fallback = this.getFallbackByScope(this.fallback, {
      scope: 'user'
    }, [this.fallback[0]]);
    return this.fetch(this.getVersionPath(path, oldVersionName), path, fallback, 'update', false, [{
      path: 'path',
      value: this.getVersionPath(path, newVersionName)
    }]);
  }
  resetVersion(path, version) {
    // only want to delete the most "local"?
    const fallback = this.getFallbackByScope(this.fallback, {
      scope: 'user'
    }, [this.fallback[0]]);
    return this.fetch(this.getVersionPath(path, version), path, fallback, 'delete', true);
  }
  // end current version stuff

  // end current

  // interface functions for setting a specific shared setting
  getSharedProfile(state, profile, path, opts) {
    const fallback = this.getFallbackByScope(this.fallback, opts);
    return this.getIf(state, path, this.getRsrcPath(path, profile), fallback, opts);
  }
  async setSharedProfile(profile, path, value, opts) {
    const fallback = this.getFallbackByScope(this.fallback, opts);
    return this.updateOrCreate(path, this.getRsrcPath(path, profile), fallback, value);
  }
  resetSharedProfile(profile, path, opts) {
    const fallback = this.getFallbackByScope(this.fallback, opts);
    return this.fetch(this.getRsrcPath(path, profile), path, fallback, 'delete', true);
  }

  // shared version stuff
  getSharedVersion(state, path, version, opts) {
    const fallback = this.getFallbackByScope(this.fallback, opts);
    return this.getIf(state, path, this.getVersionPath(path, version), fallback, opts);
  }
  getSharedVersions(state, path, opts) {
    const vPath = this.getVersionPath(path);
    const fallback = this.getFallbackByScope(this.fallback, opts);
    if (opts?.cacheOnly !== true && (this.shouldFetchChildren(state, vPath, fallback, opts) || opts?.noCache === true)) {
      return this.fetch(vPath, path, fallback, 'list', false, opts).then(rawVersions => this.prepareVersionList(path, fallback, rawVersions));
    }
    return this.prepareVersionList(path, fallback, this.getLocalChildren(state, vPath, path, fallback));
  }
  async setSharedVersion(path, version, value, opts) {
    const fallback = this.getFallbackByScope(this.fallback, opts);
    return this.updateOrCreate(path, this.getVersionPath(path, version), fallback, value);
  }
  renameSharedVersion(path, oldVersionName, newVersionName, opts) {
    const fallback = this.getFallbackByScope(this.fallback, opts);
    return this.fetch(this.getVersionPath(path, oldVersionName), path, fallback, 'update', false, [{
      path: 'path',
      value: this.getVersionPath(path, newVersionName)
    }]);
  }
  resetSharedVersion(path, version, opts) {
    // only want to delete the most "local"?
    const fallback = this.getFallbackByScope(this.fallback, opts);
    return this.fetch(this.getVersionPath(path, version), path, fallback, 'delete', true);
  }
  // end shared version stuff
  // end interface functions for a shared setting

  // interface functions for setting a specific profile for the user
  // these set the most specific fallback
  getUserProfile(state, profile, path, opts) {
    const fallback = this.getFallbackByScope(this.fallback, {
      scope: 'user'
    }, [this.fallback[0]]);
    return this.getIf(state, path, this.getRsrcPath(path, profile), fallback, opts);
  }
  async setUserProfile(profile, path, value, opts) {
    const fallback = this.getFallbackByScope(this.fallback, {
      scope: 'user'
    }, [this.fallback[0]]);
    return this.updateOrCreate(path, this.getRsrcPath(path, profile), fallback, value);
  }
  getUserVersion(state, path, version, opts) {
    return this.getVersion(state, path, version, opts);
  }
  getUserVersions(state, path, opts) {
    return this.getVersions(state, path, opts);
  }
  setUserVersion(path, version, value, opts) {
    return this.setVersion(path, version, value, opts);
  }
  renameUserVersion(path, oldVersionName, newVersionName, opts) {
    return this.renameVersion(path, oldVersionName, newVersionName, opts);
  }
  resetUserVersion(path, version) {
    return this.resetVersion(path, version);
  }
  resetUserProfile(profile, path) {
    const fallback = this.getFallbackByScope(this.fallback, {
      scope: 'user'
    }, [this.fallback[0]]);
    return this.fetch(this.getRsrcPath(path, profile), path, fallback, 'delete', true);
  }
  // end interface functions for a specific profile
}
export default ApiUserSettingsDelegate;